'use client';

import { useEffect, useMemo, useState } from 'react';
import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

type TabValue = 'popis-produktu' | 'technicke-parametre';

export const ProductSwitch = ({ product }: { product: ProductDetailData }) => {
  const hasDescription = !!product.description;

  const defaultTab: TabValue = useMemo(
    () => (hasDescription ? 'popis-produktu' : 'technicke-parametre'),
    [hasDescription],
  );

  const [tab, setTab] = useState<TabValue>(defaultTab);

  useEffect(() => {
    const applyHash = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash === 'technicke-parametre') setTab('technicke-parametre');
      if (hash === 'popis-produktu' && hasDescription) setTab('popis-produktu');
    };

    applyHash();
    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, [hasDescription]);

  return (
    <Tabs
      value={tab}
      onValueChange={v => setTab(v as TabValue)}
      className="flex w-full max-w-[913px] flex-col items-start justify-start"
    >
      <TabsList className="grid h-fit w-full grid-cols-2 rounded-none bg-transparent p-0 shadow-none">
        {hasDescription && (
          <TabsTrigger
            value="popis-produktu"
            aria-controls="popis-produktu"
            className={cn(
              'col-span-1 flex w-full items-center justify-center rounded-none border border-x-transparent border-t-transparent py-4 text-center font-heading text-base font-medium uppercase text-foreground !shadow-none sm:text-xl xl:text-2xl',
              'ea-product-switch__tabs-trigger',
            )}
          >
            Popis produktu
          </TabsTrigger>
        )}

        <TabsTrigger
          value="technicke-parametre"
          aria-controls="technicke-parametre"
          className={cn(
            'col-span-1 flex w-full items-center justify-center rounded-none border border-x-transparent border-t-transparent py-4 text-center font-heading text-base font-medium uppercase text-foreground !shadow-none sm:text-xl xl:text-2xl',
            'ea-product-switch__tabs-trigger',
          )}
        >
          Technické parametre
        </TabsTrigger>
      </TabsList>

      <TabsContent
        id="popis-produktu"
        value="popis-produktu"
        className="pt-8 sm:pt-10 md:pt-12 lg:pt-16"
      >
        <h2
          data-ea-visual-tag="h4"
          className={cn(
            'font-heading uppercase',
            'mb-4 text-2xl font-medium text-foreground sm:mb-6 sm:text-3xl lg:mb-8 lg:text-4xl',
          )}
        >
          Popis produktu
        </h2>
        <HtmlRendererRaw
          className={cn('w-full', 'ea-product-switch__content')}
          html={product.description}
        />
      </TabsContent>

      <TabsContent
        id="technicke-parametre"
        value="technicke-parametre"
        className="w-full pt-8 sm:pt-10 md:pt-12 lg:pt-16"
      >
        <h2
          data-ea-visual-tag="h4"
          className={cn(
            'font-heading uppercase',
            'mb-4 text-2xl font-medium text-foreground sm:mb-6 sm:text-3xl lg:mb-8 lg:text-4xl',
          )}
        >
          Technické parametre
        </h2>

        <div className="flex w-full flex-col items-start justify-start gap-3">
          {product.attributes?.map(attribute => (
            <div key={attribute.name} className="w-full">
              <div className="flex w-full items-start justify-start gap-6">
                <span className="min-w-32 max-w-32 text-sm font-light text-[#969696] sm:min-w-52 sm:text-base lg:min-w-[200px] xl:min-w-[300px] xl:text-lg">
                  {attribute.name}
                </span>
                <p
                  className={cn(
                    'text-sm font-light sm:text-base lg:text-lg',
                    attribute.name === 'Farba' || attribute.name === 'Veľkosť rámu'
                      ? 'text-foreground'
                      : 'text-primary',
                  )}
                >
                  {attribute.values?.map((value, index) => (
                    <span key={`${attribute.name}-${value.name}`}>
                      {value.name}
                      {index < (attribute.values?.length ?? 0) - 1 ? ', ' : ''}
                    </span>
                  ))}
                </p>
              </div>
              <Separator orientation="horizontal" className="bg-[#D0D0D0]" />
            </div>
          ))}
        </div>
      </TabsContent>
    </Tabs>
  );
};
